import React, { useCallback, useEffect, useRef, useState } from 'react';
import MessageBubble from './components/MessageBubble.jsx';
import ChatComposer from './components/ChatComposer.jsx';
import { RotateCcw } from 'lucide-react';

const YOOLIA_ICON_URL = 'https://asset.yooldigital.com/api/assets/de2733b1-1a00-4dcf-ac06-026103029df6/de2733b1-1a00-4dcf-ac06-026103029df6.svg';
const YOOLIA_ISOTIPO_URL = 'https://asset.yooldigital.com/api/assets/4770db76-7851-4f41-a926-12ff22fce650/4770db76-7851-4f41-a926-12ff22fce650.svg';

const createId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const SUGGESTED_QUESTIONS = [
  '¿Cuántos contratos se crearon este mes?',
  'Últimos 10 pagos realizados',
  '¿Cuántas cotizaciones hay pendientes?',
  'Suscripciones VirtualPos activas',
  '¿Cuántos certificados se emitieron esta semana?',
  'Compañías con más contrataciones este año',
];

const AiChatPage = () => {
  const [threadId, setThreadId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView?.({ block: 'end', behavior: 'smooth' });
  }, [messages, isSending]);

  const handleNewConversation = useCallback(() => {
    setThreadId(null);
    setMessages([]);
    setError(null);
  }, []);

  const sendMessage = useCallback(async (text) => {
    const content = String(text || '').trim();
    if (!content || isSending) return;

    setIsSending(true);
    setError(null);

    const userMsgId = createId();
    setMessages((prev) => [
      ...prev,
      { id: userMsgId, role: 'user', content, createdAt: new Date().toISOString() },
    ]);

    try {
      // Envía las últimas 6 entradas (3 turnos) como historial
      const history = messages
        .slice(-6)
        .map((m) => ({ role: m.role, content: m.content }));

      const res = await fetch('/api/ai-chat/message', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ threadId, message: content, history }),
      });

      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || `Error ${res.status}`);

      const nextThreadId = body?.threadId;
      if (nextThreadId && nextThreadId !== threadId) setThreadId(nextThreadId);

      const assistant = body?.assistantMessage;
      setMessages((prev) => [
        ...prev,
        {
          id: assistant?.id || createId(),
          role: 'assistant',
          content: String(assistant?.content || ''),
          createdAt: assistant?.createdAt || new Date().toISOString(),
          sources: assistant?.sources || [],
          meta: assistant?.meta || {},
        },
      ]);
    } catch (e) {
      setError(e?.message ? String(e.message) : 'Error al enviar mensaje');
    } finally {
      setIsSending(false);
    }
  }, [isSending, threadId]);

  return (
    <div className="flex min-h-[calc(100vh-64px)] flex-col bg-slate-50 dark:bg-[#0A1120]">

      {/* Header */}
      <div className="sticky top-0 z-10 border-b border-slate-200/60 bg-white/80 backdrop-blur-xl px-6 py-3 dark:border-slate-700/60 dark:bg-[#0A1120]/80">
        <div className="mx-auto flex max-w-3xl items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#007D6E]/10 dark:bg-[#007D6E]/20">
              <img src={YOOLIA_ISOTIPO_URL} alt="YOOLIA" className="h-4 w-4" />
            </div>
            <div>
              <h1 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                YOOLIA
              </h1>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                YOOL Inteligencia Artificial para consultas en lenguaje natural
              </p>
            </div>
          </div>

          {messages.length > 0 && (
            <button
              type="button"
              onClick={handleNewConversation}
              className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
            >
              <RotateCcw className="h-3 w-3" />
              Nueva conversación
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-3xl px-4 py-6">

          {messages.length === 0 ? (
            /* Empty state */
            <div className="flex flex-col items-center pt-12 pb-6">
              <img src={YOOLIA_ICON_URL} alt="YOOLIA" className="mb-5 h-16 w-auto" />
              <h2 className="mb-2 text-xl font-semibold text-slate-900 dark:text-slate-100">
                ¿Qué quieres consultar con YOOLIA?
              </h2>
              <p className="mb-8 max-w-md text-center text-sm text-slate-500 dark:text-slate-400">
                Haz preguntas sobre contratos, pagos, cotizaciones, certificados o cualquier dato del sistema Yool con ayuda de YOOLIA.
              </p>

              <div className="grid w-full gap-2 sm:grid-cols-2">
                {SUGGESTED_QUESTIONS.map((q) => (
                  <button
                    key={q}
                    type="button"
                    onClick={() => sendMessage(q)}
                    className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-left text-sm text-slate-700 transition-colors hover:border-[#007D6E]/40 hover:bg-[#007D6E]/5 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:border-[#007D6E]/40 dark:hover:bg-[#007D6E]/10"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            /* Conversation */
            <div className="space-y-5">
              {messages.map((m) => (
                <MessageBubble key={m.id} message={m} />
              ))}
              {isSending && (
                <MessageBubble
                  message={{
                    id: 'pending',
                    role: 'assistant',
                    content: '',
                    createdAt: new Date().toISOString(),
                  }}
                  isPending
                />
              )}
            </div>
          )}

          <div ref={bottomRef} className="h-4" />
        </div>
      </div>

      {/* Input */}
      <div className="border-t border-slate-200/60 bg-white/80 backdrop-blur-xl px-4 py-3 dark:border-slate-700/60 dark:bg-[#0A1120]/80">
        {error && (
          <div className="mx-auto mb-3 max-w-3xl rounded-xl border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-950/40 dark:text-red-400">
            {error}
          </div>
        )}
        <div className="mx-auto max-w-3xl">
          <ChatComposer disabled={isSending} onSend={sendMessage} />
        </div>
      </div>
    </div>
  );
};

export default AiChatPage;
